// src/utils/progress.js

/**
 * Returns { start, end } Date objects for given period + baseDate string.
 */
export function getPeriodBoundaries(period, baseDateStr) {
  if (period === "weekly") {
    const end = new Date(baseDateStr + "T23:59:59.999");
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    return { start, end };
  }
  if (period === "monthly") {
    const [year, month] = baseDateStr.split("-").map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0, 23, 59, 59, 999);
    return { start, end };
  }
  // yearly
  const year = Number(baseDateStr);
  return {
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31, 23, 59, 59, 999),
  };
}

/**
 * Filters entries whose date falls between start and end.
 */
export function filterEntriesByPeriod(entries, period, baseDateStr) {
  const { start, end } = getPeriodBoundaries(period, baseDateStr);
  return entries.filter((e) => {
    let d = null;
    if (e.createdAt?.toDate) d = e.createdAt.toDate();
    else if (e.date) d = new Date(e.date);
    if (!d) return false;
    return d >= start && d <= end;
  });
}

/**
 * Computes raw progress for a single goal over a period.
 * UŻYWA TYLKO goal.uid JAKO KLUCZA DO ENTRIES!
 */
export function computeProgress(entries, goal, period, baseDateStr) {
  const slice = filterEntriesByPeriod(entries, period, baseDateStr);
  if (slice.length === 0) return 0;
  const field = goal.uid || goal.field || goal.name || goal.question; // zawsze uid!
  const type = goal.calcType;
  let sum = 0, count = 0;

  // Średnia (im więcej tym lepiej)
  if (type === "average") {
    slice.forEach(e => {
      const v = e[field];
      if (v !== undefined && v !== null && v !== "" && !isNaN(Number(v))) {
        sum += Number(v);
        count++;
      }
    });
    return count > 0 ? sum / count : 0;
  }

  // Średnia (im mniej tym lepiej)
  if (type === "averageLow") {
    slice.forEach(e => {
      const v = e[field];
      if (v !== undefined && v !== null && v !== "" && !isNaN(Number(v))) {
        sum += Number(v);
        count++;
      }
    });
    return count > 0 ? sum / count : 0;
  }

  // Ilość (sumuje liczby lub wystąpienia true dla checkboxów)
  if (type === "quantityMin" || type === "cap") {
    slice.forEach(e => {
      const v = e[field];
      sum += typeof v === "boolean" ? (v ? 1 : 0) : Number(v) || 0;
    });
    return sum;
  }

  // Maksymalna wartość
  if (type === "maxValue") {
    let max = null;
    slice.forEach(e => {
      const v = e[field];
      if (v !== undefined && v !== null && v !== "" && !isNaN(Number(v))) {
        const num = Number(v);
        if (max === null || num > max) max = num;
      }
    });
    return max !== null ? max : 0;
  }

  // Dni spełniające warunek (np. powyżej min)
  if (type === "daysMetThreshold") {
    const { min } = goal[period];
    let daysMet = 0;
    slice.forEach(e => {
      const v = e[field];
      if (v !== undefined && v !== null && v !== "" && Number(v) >= min) {
        daysMet++;
      }
    });
    return daysMet;
  }

  // Passa (najdłuższa seria dni spełniających warunek)
  if (type === "streak") {
    const { min } = goal[period];
    const sorted = [...slice].sort((a, b) => {
      const da = a.createdAt?.toDate ? a.createdAt.toDate() : (a.date ? new Date(a.date) : null);
      const db = b.createdAt?.toDate ? b.createdAt.toDate() : (b.date ? new Date(b.date) : null);
      return da - db;
    });
    let maxStreak = 0;
    let currStreak = 0;
    sorted.forEach(e => {
      const v = e[field];
      if (v !== undefined && v !== null && v !== "" && Number(v) >= min) {
        currStreak++;
        if (currStreak > maxStreak) maxStreak = currStreak;
      } else {
        currStreak = 0;
      }
    });
    return maxStreak;
  }

  // fallback (nie powinno się zdarzyć)
  return 0;
}

/**
 * Converts raw progress → percentage [0–100].
 */
export function computePercentage(progress, goal, period) {
  const { min, opt } = goal[period];
  const type = goal.calcType;

  // Średnia – im więcej tym lepiej
  if (type === "average") {
    if (opt <= min) return 0;
    return Math.max(0, Math.min(100, ((progress - min) / (opt - min)) * 100));
  }
  // Średnia – im mniej tym lepiej
  if (type === "averageLow") {
    if (min <= opt) return 0;
    return Math.max(0, Math.min(100, ((min - progress) / (min - opt)) * 100));
  }
  // Ilość, ilość sumowana, dni spełniające warunek, streak
  if (type === "quantityMin" || type === "cap" || type === "daysMetThreshold" || type === "streak") {
    return opt > 0 ? Math.min(100, (progress / opt) * 100) : 0;
  }
  // Maksymalna wartość
  if (type === "maxValue") {
    return progress >= opt ? 100 : (progress / opt) * 100;
  }
  // fallback
  return 0;
}
